import { UpgradeSubscriptionModal } from '@components'

import {
  Icon,
  IconButtonProps,
  NavItem,
  NavItemRenderProps,
  NavListItemWithIconButtons,
  Select,
  SkeletonBox,
  useModal,
} from '@billy/design-system'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { customerModalTypeMap } from '@features/contacts/constants/customerModalTypeMap'
import { CustomerModal } from '@features/contacts/elements/CustomerModal'
import { DeleteContactConfirmationModal } from '@features/contacts/elements/DeleteContactConfirmationModal'
import { CustomerModalSubmitActionType } from '@features/contacts/enums/customerModalSubmitActionType'
import { CustomerType as ModalCustomerType } from '@features/contacts/enums/customerType'
import { useFetchContactById } from '@features/contacts/hooks/useFetchContactById'
import { Contact } from '@features/contacts/types/contact'
import { SelectedContact } from '@features/contacts/types/SelectedContact'
import { InvoiceFormReceiver } from '@features/invoices/types/invoiceForm/invoiceFormReceiver'

import { ModalId } from '../../../../../../../../../../enums/modalId'
import { QueryKeys } from '../../../../../../../../../../enums/queryKeys'
import { SortDirection } from '../../../../../../../../../../enums/sortDirection'
import { TrackingContext } from '../../../../../../../../../../enums/trackingContext'
import { useSubscriptionLimits, useWatch } from '../../../../../../../../../../hooks'
import { useFetchContactsInfinite } from '../../../../../../../../../contacts/hooks/useFetchContactsInfinite'
import { ContactsSortProperty } from '../../../../../../../../../contacts/query-api'
import { useInvoiceEditorForm } from '../../../../../../contexts/invoiceEditorFormContext'
import { useIsInvoiceEditorElementRestricted } from '../../../../../../hooks/useIsInvoiceEditorElementRestricted'
import { useUpdateReceiverData } from '../../../../../../hooks/useUpdateReceiverData'
import { useUpdateReceiverDependentValues } from '../../../../../../hooks/useUpdateReceiverDependentValues'
import { useUpdateReceiverOnCustomerSave } from '../../../../../../hooks/useUpdateReceiverOnCustomerSave'
import { useUpdateDependentLineValues } from '../../hooks/useUpdateDependentLineValues'
import { InvoiceReceiverDropdownFooter } from './elements/InvoiceReceiverDropdownFooter'
import { findContactById } from './utils/findContactById'
import { getReceiverIcon } from './utils/getReceiverIcon'

const CustomerModalId = ModalId.CustomerModal
const SKELETON_BOX_HEIGHT = 40 // matches the height of the input
const CONTACTS_PAGE_SIZE = 100
// Pixels before the dropdown bottom that trigger fetching the next page.
const DROPDOWN_SCROLL_END_OFFSET = 50

// Identity filter passed to the Select. Filtering already happens server-side via
// `searchQuery`, so the Select must not re-filter the items on top of that.
const keepAllItems = (items: NavItem<InvoiceFormReceiver>[]): NavItem<InvoiceFormReceiver>[] => items

type CustomerModalState = {
  selectedCustomer: Contact | undefined
  selectedCustomerType: ModalCustomerType
  submitActionType: CustomerModalSubmitActionType
}

const defaultCustomerModalState: CustomerModalState = {
  selectedCustomer: undefined,
  selectedCustomerType: ModalCustomerType.Company,
  submitActionType: CustomerModalSubmitActionType.Create,
}

interface IconButtonActionProps extends IconButtonProps {
  onItemClick: (customerId?: string) => void
}

export const InvoiceReceiver = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [isSelectOpen, setIsSelectOpen] = useState(false)
  const [customerModalState, setCustomerModalState] = useState<CustomerModalState>(defaultCustomerModalState)
  const [selectedContact, setSelectedContact] = useState<SelectedContact | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const {
    contacts,
    isLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch: refetchContacts,
  } = useFetchContactsInfinite({
    isCustomer: true,
    searchQuery,
    pageSize: CONTACTS_PAGE_SIZE,
    sortProperty: ContactsSortProperty.Name,
    sortDirection: SortDirection.Asc,
  })

  const hasLoadedOnceRef = useRef(false)
  useEffect(() => {
    if (!isLoading) {
      hasLoadedOnceRef.current = true
    }
  }, [isLoading])
  const { handleReceiverChange: handleReceiverChangeLineValues } = useUpdateDependentLineValues()
  const { handleReceiverUpdate: handleReceiverUpdateDependantValues } = useUpdateReceiverDependentValues()
  const { updateReceiverData } = useUpdateReceiverData()
  const { updateReceiverOnCustomerSave, isProcessing } = useUpdateReceiverOnCustomerSave()
  const { invoiceEditorForm, trackInvoiceEditor } = useInvoiceEditorForm()
  const { hasExceededContactsLimit, isLoading: isSubscriptionLimitsLoading } = useSubscriptionLimits()
  const isDisabled = useIsInvoiceEditorElementRestricted('receiver', 'disabled')

  const { FormItem, resetField, control } = invoiceEditorForm

  const selectedReceiver = useWatch({ control, name: 'receiver' })
  const isSelectedReceiverInList = contacts.some((contact) => contact.id === selectedReceiver?.id)

  const { contact: selectedReceiverContact } = useFetchContactById(selectedReceiver?.id, {
    enabled: !!selectedReceiver?.id && !isSelectedReceiverInList,
  })

  useEffect(() => {
    if (selectedReceiver?.externalId && !selectedReceiver?.id) {
      updateReceiverData()
    }
  }, [selectedReceiver?.externalId, selectedReceiver?.id, updateReceiverData])

  const resetCustomerModalState = useCallback(() => {
    setCustomerModalState(defaultCustomerModalState)
  }, [])

  const { open: openCustomerModal } = useModal(CustomerModalId, { onClose: resetCustomerModalState })
  const { open: openDeleteContactConfirmationModal } = useModal(ModalId.DeleteContactConfirmationModal)

  // Server-side search already filters via `searchQuery`. We only prepend the currently selected
  // contact when it's not in the loaded pages, so the Select can still display its name.
  const mergedContacts = useMemo(() => {
    if (!selectedReceiverContact || isSelectedReceiverInList) {
      return contacts
    }
    return [selectedReceiverContact, ...contacts]
  }, [contacts, isSelectedReceiverInList, selectedReceiverContact])

  const contactItems: NavItem<InvoiceFormReceiver>[] = useMemo(
    () =>
      mergedContacts.map((contact) => ({
        id: contact?.id,
        children: contact?.name,
        iconLeft: getReceiverIcon(contact),
        value: contact,
      })),
    [mergedContacts],
  )

  const closeDropdown = useCallback(() => {
    setIsSelectOpen(false)
  }, [])

  const handleDeleteContactClick = useCallback(
    (id?: string) => {
      const selectedContact = contactItems.find((contact) => contact.id === id)
      if (selectedContact) {
        setSelectedContact({ id: selectedContact.id, name: selectedContact.value.name })
        closeDropdown()
        openDeleteContactConfirmationModal()
      }
    },
    [closeDropdown, contactItems, openDeleteContactConfirmationModal],
  )

  const handleEditIconClick = useCallback(
    (customerId?: string) => {
      const contact = findContactById(customerId, mergedContacts)

      if (!contact) {
        return
      }

      setCustomerModalState({
        selectedCustomer: contact,
        selectedCustomerType: customerModalTypeMap[contact.type],
        submitActionType: CustomerModalSubmitActionType.Edit,
      })
      openCustomerModal()
      closeDropdown()
    },
    [closeDropdown, mergedContacts, openCustomerModal],
  )

  const iconButtonItems: IconButtonActionProps[] = useMemo(
    () => [
      { icon: 'squarePen', onItemClick: handleEditIconClick },
      { icon: 'trash', onItemClick: handleDeleteContactClick },
    ],
    [handleDeleteContactClick, handleEditIconClick],
  )

  const handleDropdownOpen = useCallback(() => {
    trackInvoiceEditor('Form - Customer - dropdown opened')
    setIsSelectOpen(true)
  }, [trackInvoiceEditor])

  const handleDropdownClose = useCallback(() => {
    closeDropdown()
    setSearchQuery('')
    // Drop the infinite-pages cache so the next opening starts fresh on page 1
    // instead of replaying all the pages the user had scrolled through.
    queryClient.removeQueries([QueryKeys.ContactsAndContactPersons, 'infinite'])
  }, [closeDropdown, queryClient])

  const handleDropdownScrollEnd = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const clearSelectedContactId = () => {
    setSelectedContact(null)
  }

  const handleDeleteContactSuccess = (deletedContactId: string) => {
    if (selectedContact?.id === deletedContactId) {
      // the value reset might be ignored with undefined, hence resetting with null
      resetField('receiver', { defaultValue: null })
    }
    clearSelectedContactId()
    refetchContacts()
  }

  const handleDeleteContactConfirmationModalClose = useCallback(() => {
    clearSelectedContactId()
  }, [])

  const handleFooterButtonClick = useCallback(
    (customerType: ModalCustomerType) => {
      setCustomerModalState({
        selectedCustomer: undefined,
        selectedCustomerType: customerType,
        submitActionType: CustomerModalSubmitActionType.Create,
      })
      closeDropdown()
    },
    [closeDropdown],
  )

  const handleCustomerOnSubmit = useCallback(
    async (customer: Contact) => {
      await updateReceiverOnCustomerSave(customer.id)
    },
    [updateReceiverOnCustomerSave],
  )

  return (
    <>
      <FormItem
        name="receiver"
        label={t('external_invoices.editor.form.field.receiver.label')}
        render={({ field: { onChange, value, ...rest } }) => {
          // Only show the full-width skeleton on the very first load (before any contacts
          // page has ever been received) or while a customer save is in flight. Once a
          // first response has come back, never replace the Select with a skeleton:
          //   - typing in the search input changes `queryKey` and flips `isLoading` back
          //     to `true` (without `keepPreviousData`); we want the Select to stay mounted
          //     and surface the loading state through `dropdownFetching` instead.
          //   - closing the dropdown calls `queryClient.removeQueries` below, which also
          //     briefly flips `isLoading` back to `true`.
          return isProcessing || (isLoading && !hasLoadedOnceRef.current) ? (
            <SkeletonBox height={SKELETON_BOX_HEIGHT} fullWidth />
          ) : (
            <>
              <Select
                {...rest}
                disabled={isDisabled}
                dropdownFooter={
                  <InvoiceReceiverDropdownFooter
                    hasExceededContactsLimit={Boolean(hasExceededContactsLimit)}
                    isLoading={isSubscriptionLimitsLoading}
                    onFooterButtonClick={handleFooterButtonClick}
                  />
                }
                dropdownFetching={isFetching || isFetchingNextPage}
                dropdownItemRender={(props: NavItemRenderProps<InvoiceFormReceiver>) => (
                  <NavListItemWithIconButtons iconButtons={iconButtonItems} {...props} />
                )}
                dropdownScrollEndOffset={DROPDOWN_SCROLL_END_OFFSET}
                dropdownSize="fitTrigger"
                isOpen={isSelectOpen}
                items={contactItems}
                itemsFilterFn={keepAllItems}
                // Debounced by the design-system Input (DEFAULT_DEBOUNCE_RATE = 500ms);
                // any in-flight infinite request is cancelled in useFetchContactsInfinite when this changes.
                onChangeDebounced={(event) => setSearchQuery(event.target.value)}
                onDropdownClose={handleDropdownClose}
                onDropdownOpen={handleDropdownOpen}
                onDropdownScrollEnd={handleDropdownScrollEnd}
                onSelect={async (id?: string, receiver?: InvoiceFormReceiver) => {
                  // we need undefined specifically for validation purposes and selecting can
                  // trigger onSelect with no action on the element and provide an empty object
                  onChange(!isEmpty(receiver) ? receiver : undefined)
                  handleReceiverUpdateDependantValues(receiver)
                  handleReceiverChangeLineValues(receiver)
                  trackInvoiceEditor('Form - Customer - dropdown item selected', {
                    contactId: id,
                  })
                  await updateReceiverData(receiver)
                }}
                prefix={<Icon icon={getReceiverIcon(value)} />}
                placeholder={t('external_invoices.editor.form.field.send_to.select_placeholder')}
                selectedId={value?.id}
                selectLook={false}
                size="sm"
              />
            </>
          )
        }}
      />
      <CustomerModal
        customer={customerModalState.selectedCustomer}
        defaultCustomerType={customerModalState.selectedCustomerType}
        id={ModalId.CustomerModal}
        onSubmit={handleCustomerOnSubmit}
        size="s"
        submitActionType={customerModalState.submitActionType}
      />
      <UpgradeSubscriptionModal
        id={ModalId.UpgradeSubscriptionModal}
        trackingContext={TrackingContext.CreateCustomer}
      />
      {selectedContact && (
        <DeleteContactConfirmationModal
          selectedContact={selectedContact}
          onClose={handleDeleteContactConfirmationModalClose}
          onDeleteContactSuccess={handleDeleteContactSuccess}
        />
      )}
    </>
  )
}
